/**
 * Message codes for API responses.
 * These codes can be used by the frontend to map to appropriate translations.
 */

// General
const MESSAGE_GENERAL = {
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  FEATURE_NOT_AVAILABLE: 'FEATURE_NOT_AVAILABLE',
  // Request-body/query validation failed. The offending fields ride in the
  // response's `issues` array; display sites show their own translated copy
  // for the code rather than a server-composed English sentence.
  VALIDATION_ERROR: 'VALIDATION_ERROR',
} as const

// Auth
const MESSAGE_AUTH = {
  AUTH_HEADER_REQUIRED: 'AUTH_HEADER_REQUIRED',
  AUTH_HEADER_PARSE_ERROR: 'AUTH_HEADER_PARSE_ERROR',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_DESTROYED: 'SESSION_DESTROYED',
  SESSION_CREATION_FAILED: 'SESSION_CREATION_FAILED',
  SESSION_CREATED: 'SESSION_CREATED',
  FORBIDDEN: 'FORBIDDEN',
  PASSWORD_RESET_SENT: 'PASSWORD_RESET_SENT',
  // A password-reset link that is spent, expired or was never issued. Same
  // spelling as better-auth's own `?error=` value, which the reset page reads.
  INVALID_TOKEN: 'INVALID_TOKEN',
  // Sign-in code delivery (PET-3803). Each maps to its own copy at the display
  // site so a driver whose carrier rejected the text is told what to do next
  // rather than being handed a generic failure.
  OTP_SMS_FAILED: 'OTP_SMS_FAILED',
  OTP_SMS_INVALID_NUMBER: 'OTP_SMS_INVALID_NUMBER',
  OTP_SMS_UNSUBSCRIBED: 'OTP_SMS_UNSUBSCRIBED',
  // Distinct from OTP_SMS_FAILED on purpose: the number is fine, the country
  // just isn't enabled on the Twilio account (error 21408). Telling someone to
  // check a correct number leaves them retyping it forever.
  OTP_SMS_REGION_BLOCKED: 'OTP_SMS_REGION_BLOCKED',
  // The per-address bound on texted codes, which cost money. Distinct from the
  // generic 429 copy so the display site can say "wait a moment" rather than
  // "something went wrong".
  OTP_SMS_TOO_MANY: 'OTP_SMS_TOO_MANY',
  OTP_SMS_UNAVAILABLE: 'OTP_SMS_UNAVAILABLE',
  OTP_SEND_FAILED: 'OTP_SEND_FAILED',
  PHONE_VERIFICATION_SENT: 'PHONE_VERIFICATION_SENT',
  PHONE_VERIFICATION_INVALID: 'PHONE_VERIFICATION_INVALID',
  PHONE_VERIFICATION_EXPIRED: 'PHONE_VERIFICATION_EXPIRED',
  PHONE_VERIFIED: 'PHONE_VERIFIED',
} as const

// Tenants
const MESSAGE_TENANTS = {
  TENANT_SELECTION_REQUIRED: 'TENANT_SELECTION_REQUIRED',
  NO_TENANT_ASSIGNED: 'NO_TENANT_ASSIGNED',
  INVALID_TENANT: 'INVALID_TENANT',
  TENANT_SWITCHED: 'TENANT_SWITCHED',
} as const

// Users
const MESSAGE_USERS = {
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_NO_SESSION: 'USER_NO_SESSION',
  USER_ALREADY_ACTIVE: 'USER_ALREADY_ACTIVE',
  USER_ALREADY_DISABLED: 'USER_ALREADY_DISABLED',
  USER_ACTIVATED: 'USER_ACTIVATED',
  USER_DISABLED: 'USER_DISABLED',
  USER_CREATED: 'USER_CREATED',
  USER_UPDATED: 'USER_UPDATED',
  USER_LINKED: 'USER_LINKED',
  USER_EMAIL_EXISTS: 'USER_EMAIL_EXISTS',
  USER_ALREADY_IN_TENANT: 'USER_ALREADY_IN_TENANT',
  USER_ROLE_NOT_FOUND: 'USER_ROLE_NOT_FOUND',
  USER_DRIVER_ROLE_NOT_ALLOWED: 'USER_DRIVER_ROLE_NOT_ALLOWED',
  USER_ROLE_EXCEEDS_OWN_PERMISSIONS: 'USER_ROLE_EXCEEDS_OWN_PERMISSIONS',
  USER_CANNOT_DISABLE_SELF: 'USER_CANNOT_DISABLE_SELF',
  USER_CANNOT_DELETE_SELF: 'USER_CANNOT_DELETE_SELF',
  USER_CANNOT_CHANGE_OWN_ROLE: 'USER_CANNOT_CHANGE_OWN_ROLE',
  USER_HAS_TRANSACTIONS: 'USER_HAS_TRANSACTIONS',
  USER_DELETED: 'USER_DELETED',
  USER_ACCOUNT_DELETED: 'USER_ACCOUNT_DELETED',
  INVITATION_NOT_FOUND: 'INVITATION_NOT_FOUND',
  INVITATION_EXPIRED: 'INVITATION_EXPIRED',
  INVITATION_ALREADY_USED: 'INVITATION_ALREADY_USED',
  INVITATION_REVOKED: 'INVITATION_REVOKED',
  INVITATION_SENT: 'INVITATION_SENT',
  INVITATION_INVALID_INPUT: 'INVITATION_INVALID_INPUT',
} as const

// Books (Example)
const MESSAGE_BOOKS = {
  BOOK_NOT_FOUND: 'BOOK_NOT_FOUND',
  BOOK_CREATED: 'BOOK_CREATED',
  BOOK_UPDATED: 'BOOK_UPDATED',
  BOOK_DELETED: 'BOOK_DELETED',
  BOOK_ISBN_EXISTS: 'BOOK_ISBN_EXISTS',
} as const

// Book Reviews (Example)
const MESSAGE_REVIEWS = {
  REVIEW_NOT_FOUND: 'REVIEW_NOT_FOUND',
  REVIEW_CREATED: 'REVIEW_CREATED',
  REVIEW_UPDATED: 'REVIEW_UPDATED',
  REVIEW_DELETED: 'REVIEW_DELETED',
  REVIEW_ALREADY_EXISTS: 'REVIEW_ALREADY_EXISTS',
  REVIEW_INVALID_RATING: 'REVIEW_INVALID_RATING',
} as const

// Contractors
const MESSAGE_CONTRACTORS = {
  CONTRACTOR_NOT_FOUND: 'CONTRACTOR_NOT_FOUND',
  CONTRACTOR_CREATED: 'CONTRACTOR_CREATED',
  CONTRACTOR_PHONE_REQUIRED: 'CONTRACTOR_PHONE_REQUIRED',
  CONTRACTOR_COMPANY_REQUIRED: 'CONTRACTOR_COMPANY_REQUIRED',
  CONTRACTOR_EMAIL_IN_USE: 'CONTRACTOR_EMAIL_IN_USE',
  CONTRACTOR_DISABLED: 'CONTRACTOR_DISABLED',
  CONTRACTOR_ROLE_NOT_ON_COMPANY: 'CONTRACTOR_ROLE_NOT_ON_COMPANY',
  CONTRACTOR_LAST_MASTER: 'CONTRACTOR_LAST_MASTER',
  COMMENT_NOT_FOUND: 'COMMENT_NOT_FOUND',
  COMMENT_CREATED: 'COMMENT_CREATED',
  COMMENT_UPDATED: 'COMMENT_UPDATED',
  COMMENT_DELETED: 'COMMENT_DELETED',
} as const

// Contractor Companies
const MESSAGE_CONTRACTOR_COMPANIES = {
  CONTRACTOR_COMPANY_NOT_FOUND: 'CONTRACTOR_COMPANY_NOT_FOUND',
  CONTRACTOR_COMPANY_CREATED: 'CONTRACTOR_COMPANY_CREATED',
  CONTRACTOR_COMPANY_UPDATED: 'CONTRACTOR_COMPANY_UPDATED',
  CONTRACTOR_COMPANY_NAME_EXISTS: 'CONTRACTOR_COMPANY_NAME_EXISTS',
} as const

// Projects
const MESSAGE_PROJECTS = {
  PROJECT_NOT_FOUND: 'PROJECT_NOT_FOUND',
  PROJECT_CREATED: 'PROJECT_CREATED',
  PROJECT_UPDATED: 'PROJECT_UPDATED',
  PROJECT_DELETED: 'PROJECT_DELETED',
  PROJECT_NAME_EXISTS: 'PROJECT_NAME_EXISTS',
  PROJECT_DUPLICATE_TODAY: 'PROJECT_DUPLICATE_TODAY',
  PROJECT_STAGE_NOT_FOUND: 'PROJECT_STAGE_NOT_FOUND',
  PROJECT_STAGE_NAME_EXISTS: 'PROJECT_STAGE_NAME_EXISTS',
  PROJECT_STAGE_DISABLED: 'PROJECT_STAGE_DISABLED',
  PROJECT_STAGE_LAST_OPEN: 'PROJECT_STAGE_LAST_OPEN',
  PROJECT_STAGE_ORDER_MISMATCH: 'PROJECT_STAGE_ORDER_MISMATCH',
  PROJECT_STAGES_NOT_CONFIGURED: 'PROJECT_STAGES_NOT_CONFIGURED',
  PROJECT_BOARD_ORDER_STALE: 'PROJECT_BOARD_ORDER_STALE',
  PROJECT_BIDDER_STATUS_NOT_FOUND: 'PROJECT_BIDDER_STATUS_NOT_FOUND',
  PROJECT_BIDDER_STATUS_NAME_EXISTS: 'PROJECT_BIDDER_STATUS_NAME_EXISTS',
  PROJECT_BIDDER_STATUS_DISABLED: 'PROJECT_BIDDER_STATUS_DISABLED',
  PROJECT_BIDDER_STATUS_LAST_ENABLED: 'PROJECT_BIDDER_STATUS_LAST_ENABLED',
  PROJECT_BIDDER_STATUS_ORDER_MISMATCH: 'PROJECT_BIDDER_STATUS_ORDER_MISMATCH',
  PROJECT_BIDDER_STATUSES_NOT_CONFIGURED: 'PROJECT_BIDDER_STATUSES_NOT_CONFIGURED',
  PROJECT_BIDDER_NOT_FOUND: 'PROJECT_BIDDER_NOT_FOUND',
  PROJECT_BIDDER_EXISTS: 'PROJECT_BIDDER_EXISTS',
  PROJECT_BIDDER_HAS_QUOTES: 'PROJECT_BIDDER_HAS_QUOTES',
  PROJECT_BIDDER_INVALID_CONTRACTOR: 'PROJECT_BIDDER_INVALID_CONTRACTOR',
  PROJECT_BIDDER_NOTE_NOT_FOUND: 'PROJECT_BIDDER_NOTE_NOT_FOUND',
  PROJECT_BIDDER_NOTE_NOT_AUTHOR: 'PROJECT_BIDDER_NOTE_NOT_AUTHOR',
} as const

// Quotes
const MESSAGE_QUOTES = {
  QUOTE_NOT_FOUND: 'QUOTE_NOT_FOUND',
  QUOTE_CREATED: 'QUOTE_CREATED',
  QUOTE_UPDATED: 'QUOTE_UPDATED',
  QUOTE_DELETED: 'QUOTE_DELETED',
  QUOTE_STATUS_UPDATED: 'QUOTE_STATUS_UPDATED',
  QUOTE_VERSION_CREATED: 'QUOTE_VERSION_CREATED',
  QUOTE_INVALID_STATUS_TRANSITION: 'QUOTE_INVALID_STATUS_TRANSITION',
  QUOTE_ALREADY_HAS_STATUS: 'QUOTE_ALREADY_HAS_STATUS',
  QUOTE_IMMUTABLE_SENT: 'QUOTE_IMMUTABLE_SENT',
  QUOTE_COSTS_UPDATE_ONLY_APPROVED: 'QUOTE_COSTS_UPDATE_ONLY_APPROVED',
  QUOTE_ROUTE_VALIDATION_FAILED: 'QUOTE_ROUTE_VALIDATION_FAILED',
  QUOTE_ROUTE_NOT_FOUND: 'QUOTE_ROUTE_NOT_FOUND',
  QUOTE_ITEM_NOT_FOUND: 'QUOTE_ITEM_NOT_FOUND',
  QUOTE_LINE_ITEM_NOT_FOUND: 'QUOTE_LINE_ITEM_NOT_FOUND',
  QUOTE_CREATION_FAILED: 'QUOTE_CREATION_FAILED',
  QUOTE_VERSION_CREATION_FAILED: 'QUOTE_VERSION_CREATION_FAILED',
  QUOTE_APPROBATION_ALREADY_SET: 'QUOTE_APPROBATION_ALREADY_SET',
  QUOTE_ITEM_COST_NOT_FOUND: 'QUOTE_ITEM_COST_NOT_FOUND',
  QUOTE_ITEM_COST_CREATED: 'QUOTE_ITEM_COST_CREATED',
  QUOTE_ITEM_COST_UPDATED: 'QUOTE_ITEM_COST_UPDATED',
  QUOTE_ITEM_COST_DELETED: 'QUOTE_ITEM_COST_DELETED',
} as const

// Materials
const MESSAGE_MATERIALS = {
  MATERIAL_NOT_FOUND: 'MATERIAL_NOT_FOUND',
  MATERIAL_NAME_EXISTS: 'MATERIAL_NAME_EXISTS',
  MATERIAL_CREATED: 'MATERIAL_CREATED',
  MATERIAL_UPDATED: 'MATERIAL_UPDATED',
  MATERIAL_DISABLED: 'MATERIAL_DISABLED',
  MATERIAL_ENABLED: 'MATERIAL_ENABLED',
  MATERIAL_TYPE_NOT_FOUND: 'MATERIAL_TYPE_NOT_FOUND',
  MATERIAL_TYPE_NAME_EXISTS: 'MATERIAL_TYPE_NAME_EXISTS',
  MATERIAL_TOO_MANY_PHOTOS: 'MATERIAL_TOO_MANY_PHOTOS',
} as const

// Facilities
const MESSAGE_FACILITIES = {
  FACILITY_NOT_FOUND: 'FACILITY_NOT_FOUND',
  FACILITY_NAME_EXISTS: 'FACILITY_NAME_EXISTS',
  FACILITY_MATERIAL_NOT_FOUND: 'FACILITY_MATERIAL_NOT_FOUND',
  FACILITY_MATERIAL_ALREADY_ATTACHED: 'FACILITY_MATERIAL_ALREADY_ATTACHED',
  FACILITY_MATERIAL_PHOTO_NOT_FOUND: 'FACILITY_MATERIAL_PHOTO_NOT_FOUND',
} as const

// Orders
const MESSAGE_ORDERS = {
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  ORDER_CREATED: 'ORDER_CREATED',
  ORDER_INVALID_STATUS_TRANSITION: 'ORDER_INVALID_STATUS_TRANSITION',
  ORDER_ALREADY_COMPLETED: 'ORDER_ALREADY_COMPLETED',
  ORDER_ALREADY_CANCELLED: 'ORDER_ALREADY_CANCELLED',
  ORDER_QUOTE_NOT_APPROVED: 'ORDER_QUOTE_NOT_APPROVED',
  ORDER_ITEM_ALREADY_ORDERED: 'ORDER_ITEM_ALREADY_ORDERED',
  ORDER_INVALID_SELECTION: 'ORDER_INVALID_SELECTION',
  ORDER_HAS_COMMITTED_DISPATCHES: 'ORDER_HAS_COMMITTED_DISPATCHES',
  ORDER_NOT_READY_TO_COMPLETE: 'ORDER_NOT_READY_TO_COMPLETE',
  DISPATCH_NOT_READY_TO_COMPLETE: 'DISPATCH_NOT_READY_TO_COMPLETE',
  ORDER_ROUTE_NOT_FOUND: 'ORDER_ROUTE_NOT_FOUND',
  ORDER_ROUTE_COST_NOT_FOUND: 'ORDER_ROUTE_COST_NOT_FOUND',
  ORDER_ROUTE_COST_CREATED: 'ORDER_ROUTE_COST_CREATED',
  ORDER_ROUTE_COST_UPDATED: 'ORDER_ROUTE_COST_UPDATED',
  ORDER_ROUTE_COST_DELETED: 'ORDER_ROUTE_COST_DELETED',
  ORDER_LOCKED_FOR_COST_EDIT: 'ORDER_LOCKED_FOR_COST_EDIT',
  QUICK_ORDER_MATERIAL_NOT_IN_CATALOG: 'QUICK_ORDER_MATERIAL_NOT_IN_CATALOG',
  QUICK_ORDER_ITEM_LINES_INVALID: 'QUICK_ORDER_ITEM_LINES_INVALID',
  QUICK_ORDER_CREATED: 'QUICK_ORDER_CREATED',
  ORDER_HAS_NO_ROUTES: 'ORDER_HAS_NO_ROUTES',
} as const

// Dispatches
const MESSAGE_DISPATCHES = {
  DISPATCH_NOT_FOUND: 'DISPATCH_NOT_FOUND',
  DISPATCH_CREATED: 'DISPATCH_CREATED',
  DISPATCH_TRUCK_UNCHANGED: 'DISPATCH_TRUCK_UNCHANGED',
  DISPATCH_INVALID_STATUS_TRANSITION: 'DISPATCH_INVALID_STATUS_TRANSITION',
  DISPATCH_STATUS_CONFLICT: 'DISPATCH_STATUS_CONFLICT',
  DISPATCH_QUANTITY_FULFILLED: 'DISPATCH_QUANTITY_FULFILLED',
  DISPATCH_ZERO_RATE: 'DISPATCH_ZERO_RATE',
  DISPATCH_TARGET_HOURS_OUT_OF_RANGE: 'DISPATCH_TARGET_HOURS_OUT_OF_RANGE',
  DISPATCH_NOT_DISMISSABLE: 'DISPATCH_NOT_DISMISSABLE',
  DISPATCH_PLANNED_LOADS_HOURLY: 'DISPATCH_PLANNED_LOADS_HOURLY',
  DISPATCH_PLANNED_LOADS_BELOW_TRIPS: 'DISPATCH_PLANNED_LOADS_BELOW_TRIPS',
  DISPATCH_PLANNED_LOADS_TERMINAL: 'DISPATCH_PLANNED_LOADS_TERMINAL',
  DISPATCH_LOADS_FULL: 'DISPATCH_LOADS_FULL',
  DISPATCH_MATERIALIZE_HOURLY: 'DISPATCH_MATERIALIZE_HOURLY',
  DISPATCH_MATERIALIZE_NOT_READY: 'DISPATCH_MATERIALIZE_NOT_READY',
  DISPATCH_MATERIALIZE_UNPLANNED: 'DISPATCH_MATERIALIZE_UNPLANNED',
  DISPATCH_MATERIALIZE_DRIVER_MISMATCH: 'DISPATCH_MATERIALIZE_DRIVER_MISMATCH',
  DISPATCH_CLOCK_NOT_HOURLY: 'DISPATCH_CLOCK_NOT_HOURLY',
  DISPATCH_CLOCK_INVALID_STATUS: 'DISPATCH_CLOCK_INVALID_STATUS',
  DISPATCH_CLOCK_DRIVER_MISMATCH: 'DISPATCH_CLOCK_DRIVER_MISMATCH',
  DISPATCH_LOADS_SHORT: 'DISPATCH_LOADS_SHORT',
  DISPATCH_QTY_SHORT: 'DISPATCH_QTY_SHORT',
  DISPATCH_TRIPS_IN_PROGRESS: 'DISPATCH_TRIPS_IN_PROGRESS',
  DISPATCH_TICKETS_DRAFT: 'DISPATCH_TICKETS_DRAFT',
  ORDER_ROUTE_DOES_NOT_BELONG_TO_ORDER: 'ORDER_ROUTE_DOES_NOT_BELONG_TO_ORDER',
  DISPATCH_ROUTE_CAP_REACHED: 'DISPATCH_ROUTE_CAP_REACHED',
} as const

// PDF Parsing
const MESSAGE_PDF = {
  PDF_FILE_REQUIRED: 'PDF_FILE_REQUIRED',
  PDF_FILE_TOO_LARGE: 'PDF_FILE_TOO_LARGE',
  PDF_INVALID_FILE_TYPE: 'PDF_INVALID_FILE_TYPE',
  PDF_NO_TEXT_CONTENT: 'PDF_NO_TEXT_CONTENT',
  PDF_PARSE_FAILED: 'PDF_PARSE_FAILED',
} as const

// Driver Onboarding
const MESSAGE_DRIVER_ONBOARDING = {
  TOKEN_NOT_FOUND: 'TOKEN_NOT_FOUND',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_ALREADY_USED: 'TOKEN_ALREADY_USED',
  TOKEN_REVOKED: 'TOKEN_REVOKED',
  // An invitation the app cannot open on its own: no mailbox on it, or not an
  // owner operator's. The driver finishes it on the website instead.
  INVITE_OPENS_ON_WEB: 'INVITE_OPENS_ON_WEB',
  TOKEN_CREATED: 'TOKEN_CREATED',
  TOKEN_REUSED: 'TOKEN_REUSED',
  TOKEN_REVOKED_SUCCESS: 'TOKEN_REVOKED_SUCCESS',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TENANT_INACTIVE: 'TENANT_INACTIVE',
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
  PENDING_APPLICATION_SAVED: 'PENDING_APPLICATION_SAVED',
  ACCOUNT_CREATED: 'ACCOUNT_CREATED',
  LOGIN_LINK_SENT: 'LOGIN_LINK_SENT',
  DRIVER_EMAIL_EXISTS: 'DRIVER_EMAIL_EXISTS',
  MAGIC_TOKEN_NOT_FOUND: 'MAGIC_TOKEN_NOT_FOUND',
  MAGIC_TOKEN_EXPIRED: 'MAGIC_TOKEN_EXPIRED',
  MAGIC_TOKEN_ALREADY_USED: 'MAGIC_TOKEN_ALREADY_USED',
  PASSWORD_SET_SUCCESS: 'PASSWORD_SET_SUCCESS',
  JOIN_TOKEN_NOT_FOUND: 'JOIN_TOKEN_NOT_FOUND',
  JOIN_TOKEN_EXPIRED: 'JOIN_TOKEN_EXPIRED',
  JOIN_TOKEN_ALREADY_USED: 'JOIN_TOKEN_ALREADY_USED',
  JOIN_CONFIRMATION_SENT: 'JOIN_CONFIRMATION_SENT',
  DRIVER_JOINED: 'DRIVER_JOINED',
  DRIVER_NOT_FOUND: 'DRIVER_NOT_FOUND',
  // Blocks new dispatch assignment only; the driver keeps the job in hand.
  DRIVER_DISABLED: 'DRIVER_DISABLED',
  // An owner operator still held by their application (`OO-50`) gets no dispatch.
  DRIVER_ONBOARDING_PENDING: 'DRIVER_ONBOARDING_PENDING',
  DRIVER_UPDATED: 'DRIVER_UPDATED',
  DRIVER_PROFILE_UPDATED: 'DRIVER_PROFILE_UPDATED',
  // A terminal application is nobody's turn: the applicant cannot revise their
  // way out of a rejection, and the office reviewed the values that are on
  // file (ONB-26). They are still bound to it because every driver-facing
  // resolver joins on `satellite.driverId`, which clone-on-reject nulls on the
  // child (OO-12) — so a rejected applicant keeps resolving the dead parent
  // until they follow the resubmit link.
  ONBOARDING_APPLICATION_TERMINAL: 'ONBOARDING_APPLICATION_TERMINAL',
  // This flow declares no office-editable business record (`officeDetails`), so
  // the office details routes have nothing to write for it.
  ONBOARDING_OFFICE_DETAILS_UNSUPPORTED: 'ONBOARDING_OFFICE_DETAILS_UNSUPPORTED',
  // Another live application in the tenant already uses this address (ONB-11).
  ONBOARDING_APPLICANT_EMAIL_IN_USE: 'ONBOARDING_APPLICANT_EMAIL_IN_USE',
  // A declared vehicle already became a Fleet truck; Fleet owns its removal (OO-34).
  ONBOARDING_VEHICLE_PROMOTED: 'ONBOARDING_VEHICLE_PROMOTED',
  ONBOARDING_VEHICLE_NOT_FOUND: 'ONBOARDING_VEHICLE_NOT_FOUND',
  ONBOARDING_VEHICLE_LIMIT_REACHED: 'ONBOARDING_VEHICLE_LIMIT_REACHED',
  ONBOARDING_VEHICLE_VIN_IN_USE: 'ONBOARDING_VEHICLE_VIN_IN_USE',
  // The insurance sub-flow has started — the operator's own policy is filed, or
  // a request went to Emblem — so the office can no longer switch the path (OO-43).
  ONBOARDING_INSURANCE_PATH_LOCKED: 'ONBOARDING_INSURANCE_PATH_LOCKED',
  DRIVER_INVALID_STATUS_TRANSITION: 'DRIVER_INVALID_STATUS_TRANSITION',
  DRIVER_STATUS_UPDATED: 'DRIVER_STATUS_UPDATED',
  ONBOARDING_STEP_COMPLETED: 'ONBOARDING_STEP_COMPLETED',
  ONBOARDING_STARTED: 'ONBOARDING_STARTED',
  ONBOARDING_DATE_OF_BIRTH_REQUIRED: 'ONBOARDING_DATE_OF_BIRTH_REQUIRED',
  // The SunBiz filing or the SAFER snapshot is not on file at all, so the
  // operator cannot be approved for dispatch (OO-23). Split from the code
  // below because the remedy differs: this one is an upload, that one a review.
  ONBOARDING_BUSINESS_DOCS_MISSING: 'ONBOARDING_BUSINESS_DOCS_MISSING',
  // Both are on file but at least one is still `draft` / `pending` / `rejected`
  // / `expired` (OO-23).
  ONBOARDING_BUSINESS_DOCS_NOT_APPROVED: 'ONBOARDING_BUSINESS_DOCS_NOT_APPROVED',
  INSURANCE_PATH_SELECTED: 'INSURANCE_PATH_SELECTED',
  INSURANCE_PATH_REQUIRED: 'INSURANCE_PATH_REQUIRED',
  INSURANCE_PATH_LOCKED_PREPROVIDED: 'INSURANCE_PATH_LOCKED_PREPROVIDED',
} as const

// FMCSA
const MESSAGE_FMCSA = {
  FMCSA_API_KEY_NOT_CONFIGURED: 'FMCSA_API_KEY_NOT_CONFIGURED',
  FMCSA_CARRIER_NOT_FOUND: 'FMCSA_CARRIER_NOT_FOUND',
  FMCSA_API_ERROR: 'FMCSA_API_ERROR',
  FMCSA_INVALID_DOT_NUMBER: 'FMCSA_INVALID_DOT_NUMBER',
  // The office asked to refresh FMCSA on an application that carries no DOT
  // number. The remedy is to type one, so this is its own code rather than a
  // not-found on a lookup that was never made.
  FMCSA_DOT_NUMBER_REQUIRED: 'FMCSA_DOT_NUMBER_REQUIRED',
} as const

// Admin
const MESSAGE_ADMIN = {
  DRIVER_APPROVED_FOR_DISPATCH: 'DRIVER_APPROVED_FOR_DISPATCH',
  DRIVER_REJECTION_REASON_REQUIRED: 'DRIVER_REJECTION_REASON_REQUIRED',
  DRIVER_CANNOT_APPROVE: 'DRIVER_CANNOT_APPROVE',
} as const

// Driver Documents
const MESSAGE_DRIVER_DOCUMENTS = {
  DOCUMENT_UPLOADED: 'DOCUMENT_UPLOADED',
  DOCUMENT_OVERRIDDEN: 'DOCUMENT_OVERRIDDEN',
  DOCUMENT_NOT_FOUND: 'DOCUMENT_NOT_FOUND',
  DOCUMENT_APPROVED: 'DOCUMENT_APPROVED',
  DOCUMENT_REJECTED: 'DOCUMENT_REJECTED',
  DOCUMENT_DELETED: 'DOCUMENT_DELETED',
  DOCUMENT_TYPE_NOT_FOUND: 'DOCUMENT_TYPE_NOT_FOUND',
  DOCUMENT_FILE_TOO_LARGE: 'DOCUMENT_FILE_TOO_LARGE',
  DOCUMENT_INVALID_FILE_TYPE: 'DOCUMENT_INVALID_FILE_TYPE',
  DOCUMENT_FILE_REQUIRED: 'DOCUMENT_FILE_REQUIRED',
  DOCUMENT_REJECTION_REASON_REQUIRED: 'DOCUMENT_REJECTION_REASON_REQUIRED',
  DOCUMENT_MAX_FILES_REACHED: 'DOCUMENT_MAX_FILES_REACHED',
  DOCUMENT_FILE_NOT_FOUND: 'DOCUMENT_FILE_NOT_FOUND',
  DOCUMENT_ALREADY_APPROVED: 'DOCUMENT_ALREADY_APPROVED',
  DOCUMENT_OFFICE_PROVIDED: 'DOCUMENT_OFFICE_PROVIDED',
  // The signed agreement only enters through the agreement's own path: the
  // DocuSeal archive or the paper scan (DOC-44, INS-61), never a generic upload.
  DOCUMENT_SIGNED_AGREEMENT_NOT_UPLOADABLE: 'DOCUMENT_SIGNED_AGREEMENT_NOT_UPLOADABLE',
  DOCUMENT_NO_FILES: 'DOCUMENT_NO_FILES',
  DOCUMENT_NOT_PENDING_REVIEW: 'DOCUMENT_NOT_PENDING_REVIEW',
  DOCUMENT_READ_NOT_CONFIRMABLE: 'DOCUMENT_READ_NOT_CONFIRMABLE',
  DOCUMENT_INVALID_EXPIRATION_DATE: 'DOCUMENT_INVALID_EXPIRATION_DATE',
  DOCUMENTS_INCOMPLETE: 'DOCUMENTS_INCOMPLETE',
  DOCUMENT_PACKAGE_TOO_LARGE: 'DOCUMENT_PACKAGE_TOO_LARGE',
  DOCUMENT_OWNED_BY_APPLICATION: 'DOCUMENT_OWNED_BY_APPLICATION',
} as const

// Insurance
const MESSAGE_INSURANCE = {
  INSURANCE_SUBMISSION_CREATED: 'INSURANCE_SUBMISSION_CREATED',
  INSURANCE_NOT_READY: 'INSURANCE_NOT_READY',
  INSURANCE_ALREADY_SUBMITTED: 'INSURANCE_ALREADY_SUBMITTED',
  INSURANCE_DECISION_INVALID: 'INSURANCE_DECISION_INVALID',
  INSURANCE_SUBMISSION_NOT_FOUND: 'INSURANCE_SUBMISSION_NOT_FOUND',
  INSURANCE_AGENT_NOT_CONFIGURED: 'INSURANCE_AGENT_NOT_CONFIGURED',
  // Distinct from NOT_CONFIGURED: the agent is known but the provider rejected
  // the send, so retrying can succeed. Callers surface a retry, not a dead end.
  INSURANCE_AGENT_EMAIL_FAILED: 'INSURANCE_AGENT_EMAIL_FAILED',
  INSURANCE_VERIFICATION_CREATED: 'INSURANCE_VERIFICATION_CREATED',
  INSURANCE_APPROVED: 'INSURANCE_APPROVED',
  // The certificate of insurance is missing, still pending review, or was
  // rejected. Blocks the whole post-insurance pipeline on both paths — a COI on
  // file is the bare minimum to dispatch a truck (INS-19).
  INSURANCE_COI_NOT_APPROVED: 'INSURANCE_COI_NOT_APPROVED',
  // The SunBiz filing or the SAFER snapshot is missing, so the operator's file
  // cannot go out to Emblem. Unlike the document/transition gates the office may
  // skip (INS-13), `force` does not clear this one.
  INSURANCE_BUSINESS_DOCS_MISSING: 'INSURANCE_BUSINESS_DOCS_MISSING',
  INSURANCE_COI_UPLOADED: 'INSURANCE_COI_UPLOADED',
  INSURANCE_COI_NOT_EXPECTED: 'INSURANCE_COI_NOT_EXPECTED',
  INSURANCE_QUOTE_EXPIRED: 'INSURANCE_QUOTE_EXPIRED',
  INSURANCE_NO_ACTIVE_QUOTE: 'INSURANCE_NO_ACTIVE_QUOTE',
  // PET-3167 phase 2 — the carrier's "Ask for insurance" on a Fleet truck.
  INSURANCE_REQUEST_ALREADY_ACTIVE: 'INSURANCE_REQUEST_ALREADY_ACTIVE',
  INSURANCE_TRUCK_NO_DRIVER: 'INSURANCE_TRUCK_NO_DRIVER',
  INSURANCE_TRUCK_DOCS_NOT_APPROVED: 'INSURANCE_TRUCK_DOCS_NOT_APPROVED',
  INSURANCE_DRIVER_DOCS_NOT_APPROVED: 'INSURANCE_DRIVER_DOCS_NOT_APPROVED',
  INSURANCE_NO_ONBOARDING_OFFICE: 'INSURANCE_NO_ONBOARDING_OFFICE',
  // The office routing a request to Emblem has nobody holding the
  // `emblem` role, so nobody could open it — the link that used to carry the
  // request is gone (INS-47).
  INSURANCE_EMBLEM_DESK_UNSTAFFED: 'INSURANCE_EMBLEM_DESK_UNSTAFFED',
  // The submission was already quoted or declined, by this agent or a colleague
  // (INS-48).
  INSURANCE_SUBMISSION_ALREADY_DECIDED: 'INSURANCE_SUBMISSION_ALREADY_DECIDED',
  // Emblem quotes a carrier only once the documents it reviews itself
  // (registration, DOT inspection) are approved (INS-54).
  INSURANCE_EMBLEM_DOCUMENTS_NOT_APPROVED: 'INSURANCE_EMBLEM_DOCUMENTS_NOT_APPROVED',
  BROKER_DOCS_GENERATED: 'BROKER_DOCS_GENERATED',
  BROKER_MC_NOT_FOUND: 'BROKER_MC_NOT_FOUND',
  SIGNATURE_SENT: 'SIGNATURE_SENT',
  SIGNATURE_COMPLETED: 'SIGNATURE_COMPLETED',
  SIGNATURE_REQUEST_NOT_FOUND: 'SIGNATURE_REQUEST_NOT_FOUND',
  DOCUSEAL_NOT_CONFIGURED: 'DOCUSEAL_NOT_CONFIGURED',
  SAFETY_COORDINATOR_NOT_FOUND: 'SAFETY_COORDINATOR_NOT_FOUND',
  SAFETY_COORDINATOR_EMAIL_MISSING: 'SAFETY_COORDINATOR_EMAIL_MISSING',
  CARRIER_ADDRESS_REQUIRED: 'CARRIER_ADDRESS_REQUIRED',
  SIGNATURE_REMINDER_SENT: 'SIGNATURE_REMINDER_SENT',
  DRIVER_FILE_COMPILED: 'DRIVER_FILE_COMPILED',
  // Paper signing (INS-61), shared by both agreement pipelines. The first is
  // what Send, Remind and Restart answer while the agreement is on paper.
  AGREEMENT_SIGNING_ON_PAPER: 'AGREEMENT_SIGNING_ON_PAPER',
  AGREEMENT_NOT_ON_PAPER: 'AGREEMENT_NOT_ON_PAPER',
  AGREEMENT_ALREADY_SIGNED: 'AGREEMENT_ALREADY_SIGNED',
  AGREEMENT_ALREADY_COMPILED: 'AGREEMENT_ALREADY_COMPILED',
  AGREEMENT_PAPER_STARTED: 'AGREEMENT_PAPER_STARTED',
  AGREEMENT_PAPER_SIGNED: 'AGREEMENT_PAPER_SIGNED',
  AGREEMENT_DOCUSEAL_RESUMED: 'AGREEMENT_DOCUSEAL_RESUMED',
} as const

// Partnerships
const MESSAGE_PARTNERSHIPS = {
  PARTNERSHIP_NOT_FOUND: 'PARTNERSHIP_NOT_FOUND',
  PARTNERSHIP_CREATED: 'PARTNERSHIP_CREATED',
  PARTNERSHIP_UPDATED: 'PARTNERSHIP_UPDATED',
  PARTNERSHIP_ALREADY_EXISTS: 'PARTNERSHIP_ALREADY_EXISTS',
  PARTNERSHIP_SELF_REFERENCE: 'PARTNERSHIP_SELF_REFERENCE',
  NO_ACTIVE_PARTNERSHIP: 'NO_ACTIVE_PARTNERSHIP',
  INVALID_CARRIER_PARTNERSHIP_DIRECTION: 'INVALID_CARRIER_PARTNERSHIP_DIRECTION',
} as const

// Supervisors (PET-2999)
const MESSAGE_SUPERVISORS = {
  SUPERVISOR_NOT_FOUND: 'SUPERVISOR_NOT_FOUND',
  SUPERVISOR_REVOKED: 'SUPERVISOR_REVOKED',
  SUPERVISOR_CROSS_COMPANY: 'SUPERVISOR_CROSS_COMPANY',
  SUPERVISOR_NOT_ON_PROJECT: 'SUPERVISOR_NOT_ON_PROJECT',
  // The order's project is null, so no roster can reach it. Distinct from
  // "nobody is designated" on purpose: the fix is different, and a dispatcher
  // reading the generic message would go looking for a supervisor to add.
  ORDER_HAS_NO_PROJECT: 'ORDER_HAS_NO_PROJECT',
  // PET-3001. A live membership already exists for this (project, person).
  // Distinct from SUPERVISOR_REVOKED: re-inviting a revoked person is legal and
  // inserts a new row beside the old one (`SUP-07`), so the two cases lead to
  // opposite next steps.
  SUPERVISOR_ALREADY_ON_PROJECT: 'SUPERVISOR_ALREADY_ON_PROJECT',
  // Nothing left to re-send: the invitation was already accepted, or the
  // membership behind it has been revoked.
  SUPERVISOR_INVITATION_NOT_PENDING: 'SUPERVISOR_INVITATION_NOT_PENDING',
  // PET-3006. The contractor picked belongs to a different company than the one
  // the project names as its customer.
  SUPERVISOR_WRONG_COMPANY: 'SUPERVISOR_WRONG_COMPANY',
  SUPERVISOR_PROJECT_HAS_NO_CUSTOMER: 'SUPERVISOR_PROJECT_HAS_NO_CUSTOMER',
  // PET-3006. The contractor holds no live membership on their company's tenant:
  // a legacy office-bound contractor the backfill has not moved (`SUP-14`), or a
  // company this office no longer holds an active customer partnership with.
  SUPERVISOR_NOT_ON_COMPANY_ROSTER: 'SUPERVISOR_NOT_ON_COMPANY_ROSTER',
  // PET-3008. The company roster changes and removes supervisors and site
  // members only: this person runs the roster themselves (`SUP-41`).
  ROSTER_PERSON_NOT_MANAGEABLE: 'ROSTER_PERSON_NOT_MANAGEABLE',
  // PET-3008. The number typed on an invite does not parse as a phone number.
  ROSTER_PHONE_INVALID: 'ROSTER_PHONE_INVALID',
} as const

// Trucks & Fleet
const MESSAGE_TRUCKS = {
  TRUCK_NOT_FOUND: 'TRUCK_NOT_FOUND',
  TRUCK_DOCS_PENDING: 'TRUCK_DOCS_PENDING',
  // An owner operator's truck waits for their onboarding approval (`OO-50`).
  TRUCK_ONBOARDING_PENDING: 'TRUCK_ONBOARDING_PENDING',
  TRUCK_INVALID_INPUT: 'TRUCK_INVALID_INPUT',
  DRIVER_INVALID_INPUT: 'DRIVER_INVALID_INPUT',
  TRUCK_CREATED: 'TRUCK_CREATED',
  TRUCK_UPDATED: 'TRUCK_UPDATED',
  TRUCK_IN_USE: 'TRUCK_IN_USE',
  TRUCK_OUT_OF_SERVICE: 'TRUCK_OUT_OF_SERVICE',
  TRUCK_DOCS_PENDING_REVIEW: 'TRUCK_DOCS_PENDING_REVIEW',
  TRUCK_TYPE_NOT_FOUND: 'TRUCK_TYPE_NOT_FOUND',
  TRUCK_MAKE_NOT_FOUND: 'TRUCK_MAKE_NOT_FOUND',
  TRUCK_MAKE_NAME_EXISTS: 'TRUCK_MAKE_NAME_EXISTS',
  TRUCK_MODEL_NOT_FOUND: 'TRUCK_MODEL_NOT_FOUND',
  TRUCK_MODEL_NAME_EXISTS: 'TRUCK_MODEL_NAME_EXISTS',
  TRUCK_CAPACITY_NOT_FOUND: 'TRUCK_CAPACITY_NOT_FOUND',
  CAPACITY_UNIT_NOT_FOUND: 'CAPACITY_UNIT_NOT_FOUND',
  INVALID_CUSTOMER_TRUCK_NUMBER: 'INVALID_CUSTOMER_TRUCK_NUMBER',
  INVALID_VIN: 'INVALID_VIN',
  CUSTOMER_TRUCK_NUMBER_TAKEN: 'CUSTOMER_TRUCK_NUMBER_TAKEN',
  CUSTOMER_TRUCK_NUMBER_IMMUTABLE: 'CUSTOMER_TRUCK_NUMBER_IMMUTABLE',
} as const

// Operational Drivers
const MESSAGE_OPERATIONAL_DRIVERS = {
  OPERATIONAL_DRIVER_NOT_FOUND: 'OPERATIONAL_DRIVER_NOT_FOUND',
  OPERATIONAL_DRIVER_CREATED: 'OPERATIONAL_DRIVER_CREATED',
  OPERATIONAL_DRIVER_UPDATED: 'OPERATIONAL_DRIVER_UPDATED',
  OPERATIONAL_DRIVER_ALREADY_EXISTS: 'OPERATIONAL_DRIVER_ALREADY_EXISTS',
  // PET-616: carrier "add driver" multipart body failed identity validation
  // (missing / malformed firstName / lastName / email / phoneNumber).
  OPERATIONAL_DRIVER_INVALID_INPUT: 'OPERATIONAL_DRIVER_INVALID_INPUT',
  // PET-605: a truck is already the preferred truck of another driver (1:1
  // assignment). The 409 body carries the current occupant so the UI can
  // confirm a forced reassignment.
  TRUCK_ALREADY_ASSIGNED: 'TRUCK_ALREADY_ASSIGNED',
} as const

// Dispatch Requests
const MESSAGE_DISPATCH_REQUESTS = {
  DISPATCH_REQUEST_NOT_FOUND: 'DISPATCH_REQUEST_NOT_FOUND',
  DISPATCH_REQUEST_CREATED: 'DISPATCH_REQUEST_CREATED',
  DISPATCH_REQUEST_INVALID_STATUS_TRANSITION: 'DISPATCH_REQUEST_INVALID_STATUS_TRANSITION',
  DISPATCH_REQUEST_ALREADY_RESPONDED: 'DISPATCH_REQUEST_ALREADY_RESPONDED',
  DISPATCH_REQUEST_NOT_PENDING: 'DISPATCH_REQUEST_NOT_PENDING',
  DISPATCH_REQUEST_NOT_YOURS: 'DISPATCH_REQUEST_NOT_YOURS',
  DISPATCH_REQUEST_CANCELLED: 'DISPATCH_REQUEST_CANCELLED',
  DISPATCH_REQUEST_ASSIGNMENT_NOT_OWNED: 'DISPATCH_REQUEST_ASSIGNMENT_NOT_OWNED',
  DISPATCH_REQUEST_DISPATCH_CREATION_FAILED: 'DISPATCH_REQUEST_DISPATCH_CREATION_FAILED',
} as const

// Trips
const MESSAGE_TRIPS = {
  TRIP_NOT_FOUND: 'TRIP_NOT_FOUND',
  TRIP_ROUTE_SHARE_NOT_FOUND: 'TRIP_ROUTE_SHARE_NOT_FOUND',
  TRIP_ROUTE_SHARE_UNAVAILABLE: 'TRIP_ROUTE_SHARE_UNAVAILABLE',
  TRIP_CREATED: 'TRIP_CREATED',
  TRIP_UPDATED: 'TRIP_UPDATED',
  TRIP_INVALID_STATUS_TRANSITION: 'TRIP_INVALID_STATUS_TRANSITION',
  TRIP_STATUS_CONFLICT: 'TRIP_STATUS_CONFLICT',
  TRIP_SUBMITTED: 'TRIP_SUBMITTED',
  TRIP_APPROVED: 'TRIP_APPROVED',
  TRIP_REJECTED: 'TRIP_REJECTED',
  TRIP_INTERRUPTED: 'TRIP_INTERRUPTED',
  TRIP_RESUMED: 'TRIP_RESUMED',
  TRIP_UNDELIVERED: 'TRIP_UNDELIVERED',
  TRIP_COMPLETED: 'TRIP_COMPLETED',
  TRIP_CANCELLED: 'TRIP_CANCELLED',
} as const

// Tickets
const MESSAGE_TICKETS = {
  TICKET_NOT_FOUND: 'TICKET_NOT_FOUND',
  TICKET_CREATED: 'TICKET_CREATED',
  TICKET_UPDATED: 'TICKET_UPDATED',
  TICKET_SUBMITTED: 'TICKET_SUBMITTED',
  TICKET_APPROVED_BY_DISPATCHER: 'TICKET_APPROVED_BY_DISPATCHER',
  TICKET_APPROVED_BY_BILLING: 'TICKET_APPROVED_BY_BILLING',
  TICKET_REJECTED: 'TICKET_REJECTED',
  TICKET_ALREADY_SUBMITTED: 'TICKET_ALREADY_SUBMITTED',
  TICKET_NOT_DISPATCHER_APPROVED: 'TICKET_NOT_DISPATCHER_APPROVED',
  TICKET_NOT_REVIEWABLE_BY_BILLING: 'TICKET_NOT_REVIEWABLE_BY_BILLING',
  // A supervisor flagged the ticket and the office has not settled it (`SUP-38`).
  TICKET_SUPERVISOR_HOLD: 'TICKET_SUPERVISOR_HOLD',
  // The on-site review (`SUP-36`). NOT_ALLOWED: the state machine refuses the
  // action from the ticket's state. CLOSED: billing has decided the ticket.
  // CONFLICT: it moved between the read and the write, or the idempotency key
  // was already spent on another ticket.
  SUPERVISOR_REVIEW_NOT_ALLOWED: 'SUPERVISOR_REVIEW_NOT_ALLOWED',
  SUPERVISOR_REVIEW_CLOSED: 'SUPERVISOR_REVIEW_CLOSED',
  SUPERVISOR_REVIEW_CONFLICT: 'SUPERVISOR_REVIEW_CONFLICT',
  SUPERVISOR_REVIEW_NOTE_REQUIRED: 'SUPERVISOR_REVIEW_NOTE_REQUIRED',
  SUPERVISOR_MATERIAL_UNKNOWN: 'SUPERVISOR_MATERIAL_UNKNOWN',
  // A correction has to change the quantity or the material (`SUP-39`).
  SUPERVISOR_CORRECTION_EMPTY: 'SUPERVISOR_CORRECTION_EMPTY',
  // The on-site signature (`SUP-48`–`SUP-52`). NOT_ALLOWED: the ticket does not
  // require a signature or is not in a state a session can open from.
  SIGNATURE_SESSION_NOT_ALLOWED: 'SIGNATURE_SESSION_NOT_ALLOWED',
  // The driver's code matches none of their keys.
  SIGNATURE_CODE_INVALID: 'SIGNATURE_CODE_INVALID',
  // The driver's token or code is theirs, but not for the moment it was captured.
  SIGNATURE_CODE_EXPIRED: 'SIGNATURE_CODE_EXPIRED',
  SIGNATURE_WRONG_TICKET: 'SIGNATURE_WRONG_TICKET',
  // The session id is already another ticket's or another phone's.
  SIGNATURE_SESSION_CLOSED: 'SIGNATURE_SESSION_CLOSED',
  SIGNATURE_CODE_RATE_LIMITED: 'SIGNATURE_CODE_RATE_LIMITED',
  // A driver's token or a supervisor receipt whose signature does not verify,
  // or a key the server does not hold for that person (`SUP-49`, `SUP-55`).
  SIGNATURE_BAD_TOKEN: 'SIGNATURE_BAD_TOKEN',
  SIGNATURE_KEY_REVOKED: 'SIGNATURE_KEY_REVOKED',
  TICKET_REUPLOADED: 'TICKET_REUPLOADED',
  TICKET_ATTACHMENT_UPLOADED: 'TICKET_ATTACHMENT_UPLOADED',
  TICKET_ATTACHMENT_NOT_FOUND: 'TICKET_ATTACHMENT_NOT_FOUND',
  TICKET_FILE_NOT_FOUND: 'TICKET_FILE_NOT_FOUND',
  TICKET_TYPE_ALREADY_EXISTS: 'TICKET_TYPE_ALREADY_EXISTS',
  TICKET_NOT_EDITABLE: 'TICKET_NOT_EDITABLE',
  TICKET_EXPORT_TOO_LARGE: 'TICKET_EXPORT_TOO_LARGE',
} as const

// GPS Tracking (PET-362)
const MESSAGE_GPS = {
  GPS_READING_TOO_OLD: 'GPS_READING_TOO_OLD',
  GPS_READING_TOO_FUTURE: 'GPS_READING_TOO_FUTURE',
  GPS_DRIVER_NOT_FOUND: 'GPS_DRIVER_NOT_FOUND',
  GPS_INGESTED: 'GPS_INGESTED',
  GPS_BATCH_INGESTED: 'GPS_BATCH_INGESTED',
} as const

// Tenant Type Enforcement
const MESSAGE_TENANT_TYPE = {
  TENANT_CAPABILITY_DENIED: 'TENANT_CAPABILITY_DENIED',
} as const

// Vendors (PET-440)
const MESSAGE_VENDORS = {
  VENDOR_NOT_FOUND: 'VENDOR_NOT_FOUND',
  VENDOR_CREATED: 'VENDOR_CREATED',
  VENDOR_UPDATED: 'VENDOR_UPDATED',
  VENDOR_DELETED: 'VENDOR_DELETED',
  VENDOR_NAME_EXISTS: 'VENDOR_NAME_EXISTS',
  VENDOR_INVALID_FOR_TENANT: 'VENDOR_INVALID_FOR_TENANT',
} as const

// Roles & permissions management (PET-334)
const MESSAGE_ROLES = {
  ROLE_NOT_FOUND: 'ROLE_NOT_FOUND',
  ROLE_CREATED: 'ROLE_CREATED',
  ROLE_UPDATED: 'ROLE_UPDATED',
  ROLE_DELETED: 'ROLE_DELETED',
  ROLE_PERMISSIONS_UPDATED: 'ROLE_PERMISSIONS_UPDATED',
  ROLE_IS_SYSTEM: 'ROLE_IS_SYSTEM',
  ROLE_HAS_USERS: 'ROLE_HAS_USERS',
  ROLE_INVALID_PERMISSION: 'ROLE_INVALID_PERMISSION',
  ROLE_SOURCE_NOT_FOUND: 'ROLE_SOURCE_NOT_FOUND',
  ROLE_CANNOT_MODIFY_OWN: 'ROLE_CANNOT_MODIFY_OWN',
  ROLE_PERMISSIONS_EXCEED_OWN: 'ROLE_PERMISSIONS_EXCEED_OWN',
} as const

// Merged MESSAGE object
// Carrier onboarding (PET-604)
const MESSAGE_CARRIER_ONBOARDING = {
  CARRIER_INVITATION_SENT: 'CARRIER_INVITATION_SENT',
  CARRIER_INVITATION_EMAIL_FAILED: 'CARRIER_INVITATION_EMAIL_FAILED',
  CARRIER_APPLICATION_NOT_FOUND: 'CARRIER_APPLICATION_NOT_FOUND',
  CARRIER_APPLICATION_ALREADY_SUBMITTED: 'CARRIER_APPLICATION_ALREADY_SUBMITTED',
  CARRIER_APPLICATION_NOT_APPROVABLE: 'CARRIER_APPLICATION_NOT_APPROVABLE',
  CARRIER_APPLICATION_NOT_REJECTABLE: 'CARRIER_APPLICATION_NOT_REJECTABLE',
  CARRIER_APPLICATION_APPROVED: 'CARRIER_APPLICATION_APPROVED',
  CARRIER_APPLICATION_REJECTED: 'CARRIER_APPLICATION_REJECTED',
  CARRIER_APPLICATION_SUBMITTED: 'CARRIER_APPLICATION_SUBMITTED',
  CARRIER_APPLICATION_EXISTS_FOR_EMAIL: 'CARRIER_APPLICATION_EXISTS_FOR_EMAIL',
  CARRIER_DOCUMENT_REQUIRED: 'CARRIER_DOCUMENT_REQUIRED',
  CARRIER_COMPANY_NAME_REQUIRED: 'CARRIER_COMPANY_NAME_REQUIRED',
  // Distinct from INSURANCE_NOT_READY, which the approval gate used to reuse for
  // a missing packet: with generation queued the packet is legitimately absent
  // for a while, and "insurance isn't ready" is the wrong thing to tell a
  // reviewer who just clicked Generate (PET-3815).
  CARRIER_BROKER_PACKET_MISSING: 'CARRIER_BROKER_PACKET_MISSING',
  CARRIER_BROKER_PACKET_GENERATING: 'CARRIER_BROKER_PACKET_GENERATING',
  // Persisted on a packet job the enqueue path retired as abandoned, so the card
  // can say "nothing picked this up, press Generate again" rather than showing
  // the office an internal sentence.
  CARRIER_BROKER_PACKET_ABANDONED: 'CARRIER_BROKER_PACKET_ABANDONED',
  CARRIER_DOCUMENT_APPROVED: 'CARRIER_DOCUMENT_APPROVED',
  CARRIER_DOCUMENT_REJECTED: 'CARRIER_DOCUMENT_REJECTED',
  CARRIER_DOCUMENT_NOT_FOUND: 'CARRIER_DOCUMENT_NOT_FOUND',
  CARRIER_DOCUMENT_NOT_REVIEWABLE: 'CARRIER_DOCUMENT_NOT_REVIEWABLE',
  CARRIER_DOCUMENTS_NOT_ALL_APPROVED: 'CARRIER_DOCUMENTS_NOT_ALL_APPROVED',
  // The quote request's gate (CARR-24) — office-reviewed documents
  // approved, the ones Emblem reviews uploaded and not rejected.
  CARRIER_DOCUMENTS_NOT_READY_FOR_EMBLEM: 'CARRIER_DOCUMENTS_NOT_READY_FOR_EMBLEM',
  // The motor carrier agreement (PET-3819). `CARRIER_AGREEMENT_NOT_READY` is
  // the generic refusal from `sendCarrierForSignature`; the office never renders
  // it, because the readiness endpoint hands back a typed blocker instead and
  // the button's tooltip keys off that.
  CARRIER_AGREEMENT_NOT_READY: 'CARRIER_AGREEMENT_NOT_READY',
  CARRIER_AGREEMENT_IN_FLIGHT: 'CARRIER_AGREEMENT_IN_FLIGHT',
  CARRIER_AGREEMENT_NOT_SIGNED: 'CARRIER_AGREEMENT_NOT_SIGNED',
  CARRIER_FILE_NOT_COMPILED: 'CARRIER_FILE_NOT_COMPILED',
  CARRIER_SIGNATURE_REQUEST_NOT_FOUND: 'CARRIER_SIGNATURE_REQUEST_NOT_FOUND',
  // Declared truck-and-driver combos on the carrier wizard's fleet step
  // (PET-3167).
  FLEET_UNIT_NOT_FOUND: 'FLEET_UNIT_NOT_FOUND',
  FLEET_UNIT_LIMIT_REACHED: 'FLEET_UNIT_LIMIT_REACHED',
  FLEET_UNITS_INCOMPLETE: 'FLEET_UNITS_INCOMPLETE',
  // The cards are patched one at a time, so no single payload can hold both
  // sides of a collision the way the owner operator flow's vehicle array does
  // (`ONB-23`). These name the two application-scoped uniques so a repeated
  // value reaches the applicant as the field to fix, not as an opaque 500.
  FLEET_UNIT_VIN_DUPLICATE: 'FLEET_UNIT_VIN_DUPLICATE',
  FLEET_UNIT_DRIVER_EMAIL_DUPLICATE: 'FLEET_UNIT_DRIVER_EMAIL_DUPLICATE',
} as const

// Broker lites (PET-663)
const MESSAGE_BROKER_LITES = {
  BROKER_LITE_NOT_FOUND: 'BROKER_LITE_NOT_FOUND',
  BROKER_LITE_ALREADY_ACTIVE: 'BROKER_LITE_ALREADY_ACTIVE',
  BROKER_LITE_ALREADY_INACTIVE: 'BROKER_LITE_ALREADY_INACTIVE',
} as const

// Staged tickets — broker lite ticket ingestion (PET-665)
const MESSAGE_STAGED_TICKETS = {
  STAGED_TICKET_NOT_FOUND: 'STAGED_TICKET_NOT_FOUND',
  STAGED_TICKET_INVALID_STATUS: 'STAGED_TICKET_INVALID_STATUS',
  STAGED_TICKET_BROKER_MISMATCH: 'STAGED_TICKET_BROKER_MISMATCH',
  STAGED_TICKET_DISPATCH_ORDER_MISMATCH: 'STAGED_TICKET_DISPATCH_ORDER_MISMATCH',
  STAGED_TICKET_DISPATCH_CANCELLED: 'STAGED_TICKET_DISPATCH_CANCELLED',
  STAGED_TICKET_IMPORT_NO_FILES: 'STAGED_TICKET_IMPORT_NO_FILES',
  STAGED_TICKET_IMPORT_TOO_MANY_FILES: 'STAGED_TICKET_IMPORT_TOO_MANY_FILES',
  STAGED_TICKET_INVALID_IMPORT_FIELDS: 'STAGED_TICKET_INVALID_IMPORT_FIELDS',
  STAGED_TICKET_PDF_UNREADABLE: 'STAGED_TICKET_PDF_UNREADABLE',
  STAGED_TICKET_PDF_PASSWORD_PROTECTED: 'STAGED_TICKET_PDF_PASSWORD_PROTECTED',
} as const

// Versioned terms-of-service (PET-733)
const MESSAGE_TERMS = {
  TERMS_VERSION_STALE: 'TERMS_VERSION_STALE',
  TERMS_VERSION_NOT_FOUND: 'TERMS_VERSION_NOT_FOUND',
  TERMS_VERSION_EXISTS: 'TERMS_VERSION_EXISTS',
} as const

// Client recordings (REC-*), sent from the recorder widget on dev.
const MESSAGE_RECORDINGS = {
  // The draft or the events failed validation; the widget shows its own copy.
  RECORDING_INVALID: 'RECORDING_INVALID',
  RECORDING_TOO_LARGE: 'RECORDING_TOO_LARGE',
} as const

export const MESSAGE = {
  ...MESSAGE_GENERAL,
  ...MESSAGE_AUTH,
  ...MESSAGE_TENANTS,
  ...MESSAGE_USERS,
  ...MESSAGE_BOOKS,
  ...MESSAGE_REVIEWS,
  ...MESSAGE_CONTRACTORS,
  ...MESSAGE_CONTRACTOR_COMPANIES,
  ...MESSAGE_PROJECTS,
  ...MESSAGE_QUOTES,
  ...MESSAGE_MATERIALS,
  ...MESSAGE_FACILITIES,
  ...MESSAGE_ORDERS,
  ...MESSAGE_DISPATCHES,
  ...MESSAGE_PDF,
  ...MESSAGE_DRIVER_ONBOARDING,
  ...MESSAGE_FMCSA,
  ...MESSAGE_ADMIN,
  ...MESSAGE_DRIVER_DOCUMENTS,
  ...MESSAGE_INSURANCE,
  ...MESSAGE_PARTNERSHIPS,
  ...MESSAGE_TRUCKS,
  ...MESSAGE_OPERATIONAL_DRIVERS,
  ...MESSAGE_TENANT_TYPE,
  ...MESSAGE_DISPATCH_REQUESTS,
  ...MESSAGE_TRIPS,
  ...MESSAGE_TICKETS,
  ...MESSAGE_GPS,
  ...MESSAGE_ROLES,
  ...MESSAGE_VENDORS,
  ...MESSAGE_CARRIER_ONBOARDING,
  ...MESSAGE_BROKER_LITES,
  ...MESSAGE_STAGED_TICKETS,
  ...MESSAGE_TERMS,
  ...MESSAGE_SUPERVISORS,
  ...MESSAGE_RECORDINGS,
} as const

export type Message = (typeof MESSAGE)[keyof typeof MESSAGE]
